import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { TRAVEL_LABELS as LABELS } from './travelLabels';
import { Loader2 } from 'lucide-react';
import { adminApi } from '../../lib/adminApi';

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h3 className="font-serif text-lg font-semibold blue-text border-b border-primary pb-1">{title}</h3>
    {children}
  </div>
);

const TextField = ({ label, value, onChange, placeholder, type = 'text', testId }) => (
  <div>
    <Label className="blue-text text-sm">{label}</Label>
    <Input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="border-2 border-primary cream-bg mt-1"
      data-testid={testId}
    />
  </div>
);

const SelectField = ({ label, value, onChange, options, testId }) => (
  <div>
    <Label className="blue-text text-sm">{label}</Label>
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger className="border-2 border-primary cream-bg blue-text mt-1" data-testid={testId}>
        <SelectValue placeholder="—" />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(options).map(([val, lbl]) => (
          <SelectItem key={val} value={val}>{lbl}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const CheckboxGroup = ({ label, value = [], onChange, options, testIdPrefix }) => {
  const toggle = (v) => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
  return (
    <div>
      <Label className="blue-text text-sm">{label}</Label>
      <div className="grid grid-cols-2 gap-2 mt-2">
        {Object.entries(options).map(([val, lbl]) => (
          <div key={val} className="flex items-center space-x-2">
            <Checkbox
              id={`${testIdPrefix}-${val}`}
              checked={value.includes(val)}
              onCheckedChange={() => toggle(val)}
              data-testid={`${testIdPrefix}-${val}`}
            />
            <Label htmlFor={`${testIdPrefix}-${val}`} className="blue-text font-normal cursor-pointer text-sm">{lbl}</Label>
          </div>
        ))}
      </div>
    </div>
  );
};

const RadioField = ({ label, value, onChange, options, testIdPrefix }) => (
  <div>
    <Label className="blue-text text-sm">{label}</Label>
    <RadioGroup value={value || ''} onValueChange={onChange} className="mt-2 space-y-1">
      {Object.entries(options).map(([val, lbl]) => (
        <div key={val} className="flex items-center space-x-2">
          <RadioGroupItem value={val} id={`${testIdPrefix}-${val}`} data-testid={`${testIdPrefix}-${val}`} />
          <Label htmlFor={`${testIdPrefix}-${val}`} className="blue-text font-normal cursor-pointer text-sm">{lbl}</Label>
        </div>
      ))}
    </RadioGroup>
  </div>
);

export const EditTravelEntryDialog = ({ entry, open, onOpenChange, onSaved }) => {
  const [draft, setDraft] = useState(entry);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { setDraft(entry); setErr(null); }, [entry, open]);

  if (!entry || !draft || draft.id !== entry.id) return null;

  const set = (field, value) => setDraft(prev => ({ ...prev, [field]: value }));

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const res = await adminApi.patch(`/api/travel-form/${entry.id}`, draft);
      onSaved(res.data);
      onOpenChange(false);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const isKid = draft.isChild;
  const isFlight = !isKid && draft.travelMode !== 'other';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="cream-bg border-2 border-primary max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-travel-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl blue-text">Edit — {entry.guestName}</DialogTitle>
          <DialogDescription className="blue-text opacity-70">
            Changes are saved immediately to the guest's submission.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {isKid ? (
            <Section title="Kids Menu">
              <RadioField
                label="Kids Appetizer"
                value={draft.kidsAppetizer}
                onChange={(v) => set('kidsAppetizer', v)}
                options={LABELS.kidsAppetizer}
                testIdPrefix="edit-kids-app"
              />
              <RadioField
                label="Kids Main Course"
                value={draft.kidsMain}
                onChange={(v) => set('kidsMain', v)}
                options={LABELS.kidsMain}
                testIdPrefix="edit-kids-main"
              />
            </Section>
          ) : (
            <>
              <Section title="Contact & Travel">
                <TextField label="Phone" value={draft.phone} onChange={(v) => set('phone', v)} placeholder="+382 ..." testId="edit-phone" />
                <RadioField
                  label="Travel Mode"
                  value={draft.travelMode}
                  onChange={(v) => set('travelMode', v)}
                  options={{ flight: 'Flight', other: 'Other (not flying)' }}
                  testIdPrefix="edit-travel-mode"
                />
                <TextField label="Hotel / accommodation" value={draft.hotel} onChange={(v) => set('hotel', v)} testId="edit-hotel" />
              </Section>

              {isFlight ? (
                <>
                  <Section title="Arrival">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <SelectField label="Airport" value={draft.arrivalAirport} onChange={(v) => set('arrivalAirport', v)} options={LABELS.airport} testId="edit-arrival-airport" />
                      {draft.arrivalAirport === 'other' && (
                        <TextField label="Airport (other)" value={draft.arrivalAirportOther} onChange={(v) => set('arrivalAirportOther', v)} testId="edit-arrival-airport-other" />
                      )}
                      <TextField label="Date" type="date" value={draft.arrivalDate} onChange={(v) => set('arrivalDate', v)} testId="edit-arrival-date" />
                      <TextField label="Time" type="time" value={draft.arrivalTime} onChange={(v) => set('arrivalTime', v)} testId="edit-arrival-time" />
                      <TextField label="Flight number" value={draft.arrivalFlight} onChange={(v) => set('arrivalFlight', v)} testId="edit-arrival-flight" />
                    </div>
                  </Section>

                  <Section title="Departure">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <SelectField label="Airport" value={draft.departureAirport} onChange={(v) => set('departureAirport', v)} options={LABELS.airport} testId="edit-departure-airport" />
                      {draft.departureAirport === 'other' && (
                        <TextField label="Airport (other)" value={draft.departureAirportOther} onChange={(v) => set('departureAirportOther', v)} testId="edit-departure-airport-other" />
                      )}
                      <TextField label="Date" type="date" value={draft.departureDate} onChange={(v) => set('departureDate', v)} testId="edit-departure-date" />
                      <TextField label="Time" type="time" value={draft.departureTime} onChange={(v) => set('departureTime', v)} testId="edit-departure-time" />
                      <TextField label="Flight number" value={draft.departureFlight} onChange={(v) => set('departureFlight', v)} testId="edit-departure-flight" />
                    </div>
                  </Section>

                  <Section title="Transfers">
                    <SelectField label="Airport → Hotel" value={draft.transferArrival} onChange={(v) => set('transferArrival', v)} options={LABELS.transfer} testId="edit-transfer-arrival" />
                    {draft.transferArrival === 'other' && (
                      <TextField label="Details" value={draft.transferArrivalOther} onChange={(v) => set('transferArrivalOther', v)} testId="edit-transfer-arrival-other" />
                    )}
                    <SelectField label="Hotel → Airport" value={draft.transferDeparture} onChange={(v) => set('transferDeparture', v)} options={LABELS.transfer} testId="edit-transfer-departure" />
                    {draft.transferDeparture === 'other' && (
                      <TextField label="Details" value={draft.transferDepartureOther} onChange={(v) => set('transferDepartureOther', v)} testId="edit-transfer-departure-other" />
                    )}
                  </Section>
                </>
              ) : (
                <Section title="Arrival (non-flight)">
                  <TextField label="Arrival Date" type="date" value={draft.arrivalDate} onChange={(v) => set('arrivalDate', v)} testId="edit-arrival-date-other" />
                </Section>
              )}

              <Section title="Menu & Preferences">
                <RadioField label="Cold Appetizer" value={draft.coldAppetizer} onChange={(v) => set('coldAppetizer', v)} options={LABELS.cold} testIdPrefix="edit-cold" />
                <RadioField label="Main Course" value={draft.mainCourse} onChange={(v) => set('mainCourse', v)} options={LABELS.main} testIdPrefix="edit-main" />
                <CheckboxGroup label="Allergies" value={draft.allergies} onChange={(v) => set('allergies', v)} options={LABELS.allergy} testIdPrefix="edit-allergy" />
                {(draft.allergies || []).includes('other') && (
                  <TextField label="Allergy details" value={draft.allergiesOther} onChange={(v) => set('allergiesOther', v)} testId="edit-allergy-other" />
                )}
                <CheckboxGroup label="Drinks" value={draft.drinks} onChange={(v) => set('drinks', v)} options={LABELS.drink} testIdPrefix="edit-drink" />
                <RadioField label="Beauty Service" value={draft.beautyService} onChange={(v) => set('beautyService', v)} options={LABELS.beauty} testIdPrefix="edit-beauty" />
              </Section>

              <Section title="Notes">
                <div>
                  <Label className="blue-text text-sm">Comments</Label>
                  <Textarea
                    value={draft.comments || ''}
                    onChange={(e) => set('comments', e.target.value)}
                    className="border-2 border-primary cream-bg mt-1"
                    data-testid="edit-comments"
                  />
                </div>
              </Section>
            </>
          )}
        </div>

        {err && <p className="text-red-600 text-sm text-center" data-testid="edit-travel-error">{err}</p>}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="border-2 border-primary blue-text hover:bg-blue-50"
            data-testid="edit-travel-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={saving}
            className="blue-bg text-white hover:opacity-90"
            data-testid="edit-travel-save-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
